import { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { showToast } from '../components/Toast';
import { phoneError, normalizePhone } from '../utils/phone';
import { formatDateOnly } from '../utils/dates';
import { toDisplayHandicap, fromDisplayHandicap } from '../utils/pingolfScoring';

// Owns all data-fetching, form, modal, and mutation state for the ManageUser
// page. Extracted as a single hook (rather than split by concern) because
// nearly every handler shares `loadUserData` (the single source of truth for
// the whole page) and the shared `confirmState`/`confirmSubmitting` confirm-modal
// plumbing - splitting would mean either duplicating that infrastructure across
// hooks or wiring cross-hook coordination, which is more risk than benefit here.
// The club's tournament entry fee, charged from the Desk tab.
export const TOURNAMENT_FEE = 15;

export function useManageUser(userId, navigate) {
  const [user, setUser] = useState(null);
  const [membershipSettings, setMembershipSettings] = useState(null);
  const [application, setApplication] = useState(null);
  const [applicationHistory, setApplicationHistory] = useState([]);
  // Which documents this member has signed. Loaded here so the Overview tab can
  // answer "did they ever agree to the rules?" without a database query.
  const [signedDocuments, setSignedDocuments] = useState([]);
  // Every address known for this person, not just the primary. After a merge
  // the other account's address survives as an alias and is what Square may
  // still be billing under, so an admin looking at Contact needs to see it.
  const [memberEmails, setMemberEmails] = useState([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isTaskDelegate, setIsTaskDelegate] = useState(false);
  const [isMaintenanceOrganizer, setIsMaintenanceOrganizer] = useState(false);
  const [userBadges, setUserBadges] = useState([]);
  const [awardableBadges, setAwardableBadges] = useState([]);
  const [showAwardBadgeModal, setShowAwardBadgeModal] = useState(false);
  const [badgeToAwardId, setBadgeToAwardId] = useState('');
  const [badgeSearchTerm, setBadgeSearchTerm] = useState('');
  const [awardingBadge, setAwardingBadge] = useState(false);

  // View control states
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [showMobileTransactions, setShowMobileTransactions] = useState(false);
  const [showAllPayments, setShowAllPayments] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);

  // Modal states
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showMemberSinceSheet, setShowMemberSinceSheet] = useState(false);
  const [memberSinceDraft, setMemberSinceDraft] = useState('');
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [showTabSettingsModal, setShowTabSettingsModal] = useState(false);
  const [showHandicapSeedModal, setShowHandicapSeedModal] = useState(false);
  const [showPartOrderModal, setShowPartOrderModal] = useState(false);
  const [showGuestPassModal, setShowGuestPassModal] = useState(false);
  const [showEditTransactionModal, setShowEditTransactionModal] = useState(false);
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [showDeletePaymentModal, setShowDeletePaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [confirmState, setConfirmState] = useState(null); // { title, message, confirmLabel, danger, onConfirm }
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  // Edit profile states
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    pronouns: '',
    preferredName: '',
    hostProfile: '',
    sternUsername: '',
    matchplayUserId: '',
    ifpaPlayerId: ''
  });

  // Password reset state
  const [passwordResetResult, setPasswordResetResult] = useState(null);


  // Part order charge state
  const [partOrderForm, setPartOrderForm] = useState({
    amount: '',
    description: ''
  });

  // Guest day pass purchase state
  const [guestPassForm, setGuestPassForm] = useState({
    guestName: ''
  });

  // Desk charge state: the tournament fee (fixed price) or an "other" charge
  // (admin says what it is for and how much). `kind` is null when closed.
  const [chargeForm, setChargeForm] = useState({
    kind: null,
    amount: '',
    description: ''
  });

  // MatchPlay profile state
  const [matchplayProfile, setMatchplayProfile] = useState(null);
  const [syncingMatchplay, setSyncingMatchplay] = useState(false);

  // Form states
  const [creditsForm, setCreditsForm] = useState({
    amount: '',
    description: '',
    sendNotification: true
  });

  const [editTransactionForm, setEditTransactionForm] = useState({
    id: null,
    amount: '',
    description: ''
  });

  const [paymentForm, setPaymentForm] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMonth: new Date().toISOString().slice(0, 7) + '-01',
    amount: '50.00',
    notes: ''
  });

  const [editPaymentForm, setEditPaymentForm] = useState({
    id: null,
    paymentMonth: ''
  });


  const [tabSettingsForm, setTabSettingsForm] = useState({
    tabEnabled: false,
    tabLimit: '',
    unlimited: false
  });

  // `seed` is the MAGNITUDE only, with `seedSign` carrying the direction, so the sign is a
  // control an admin can see and press rather than a minus keystroke buried in a number
  // field. Both are in display space (strokes applied to the card); fromDisplayHandicap
  // converts back to the stored vs-par average on save.
  const [handicapSeedForm, setHandicapSeedForm] = useState({
    seed: '',
    seedSign: '+',
    seedWeight: '',
    reason: ''
  });

  const loadUserData = async () => {
    try {
      setLoading(true);

      // Load user details
      const userResponse = await apiService.getUser(userId);
      setUser(userResponse.data);

      try {
        const featureRolesResponse = await apiService.getUserFeatureRoles(userId);
        const featureRoles = featureRolesResponse.data?.featureRoles || [];
        setIsTaskDelegate(featureRoles.includes('task_management'));
        setIsMaintenanceOrganizer(featureRoles.includes('machine_maintenance'));
      } catch (err) {
        console.log('Could not load feature roles:', err.message);
      }

      try {
        const badgesResponse = await apiService.getUserBadges(userId);
        setUserBadges(badgesResponse.data || []);
      } catch (err) {
        console.log('Could not load badges:', err.message);
        setUserBadges([]);
      }

      // If user has MatchPlay ID, try to load their profile
      if (userResponse.data.matchplay_user_id) {
        try {
          const matchplayResponse = await apiService.get(`/users/${userId}/matchplay-profile`);
          setMatchplayProfile(matchplayResponse.data);
        } catch (err) {
          // Silently fail - profile sync isn't critical for page load
          console.log('Could not load MatchPlay profile:', err.message);
        }
      }

      // Load credit transactions for admin view
      // Using a high limit for admin view since they need to see full history
      // The UI already has a "View All" toggle that shows first 10 by default
      const transactionsResponse = await apiService.getCreditHistory(userId, { limit: 500 });
      // Handle both old and new response format
      const transactionsData = transactionsResponse.data.transactions || transactionsResponse.data;
      setTransactions(Array.isArray(transactionsData) ? transactionsData : []);

      // If user is a member, load membership settings
      if (userResponse.data.is_member) {
        try {
          const settingsResponse = await apiService.getMembershipSettings(userId);
          setMembershipSettings(settingsResponse.data);
        } catch (err) {
          console.log('No membership settings found');
        }
      }

      // Always load payment history (for both current and former members)
      try {
        const paymentsResponse = await apiService.getUserPaymentHistory(userId);
        setPaymentHistory(paymentsResponse.data);
      } catch (err) {
        console.log('No payment history found');
        setPaymentHistory([]);
      }

      // Load application if exists (most recent active one)
      try {
        const appResponse = await apiService.get(`/onboarding/applications/user/${userId}`);
        setApplication(appResponse.data);

        // Also load all applications for history
        const allAppsResponse = await apiService.get(`/onboarding/applications/user/${userId}/all`);
        setApplicationHistory(allAppsResponse.data);
      } catch (err) {
        // No application found - this is normal for users who haven't applied
        setApplication(null);
        setApplicationHistory([]);
      }

      try {
        const documentsResponse = await apiService.getSignedDocumentsFor(userId);
        setSignedDocuments(documentsResponse.data || []);
      } catch (err) {
        setSignedDocuments([]);
      }

      try {
        const emailsResponse = await apiService.getMemberEmails(userId);
        setMemberEmails(emailsResponse.data || []);
      } catch (err) {
        setMemberEmails([]);
      }
    } catch (err) {
      setError('Failed to load user data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      loadUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleAddCredits = async () => {
    if (!creditsForm.amount || parseFloat(creditsForm.amount) <= 0) {
      showToast('Please enter a valid amount', 'error');
      return;
    }

    try {
      await apiService.addCredits({
        userId: parseInt(userId),
        amount: parseFloat(creditsForm.amount),
        description: creditsForm.description,
        sendNotification: creditsForm.sendNotification
      });

      setShowCreditsModal(false);
      setCreditsForm({ amount: '', description: '', sendNotification: true });
      loadUserData();
      showToast('Credits added successfully', 'success');
    } catch (err) {
      showToast('Failed to add credits', 'error');
      console.error(err);
    }
  };

  const handlePartOrderCharge = async () => {
    if (!partOrderForm.amount || parseFloat(partOrderForm.amount) <= 0) {
      showToast('Please enter a valid amount', 'error');
      return;
    }

    try {
      await apiService.deductCredits({
        userId: parseInt(userId),
        amount: parseFloat(partOrderForm.amount),
        description: partOrderForm.description || 'Part order'
      });

      setShowPartOrderModal(false);
      setPartOrderForm({ amount: '', description: '' });
      loadUserData();
      showToast('Part order charged successfully', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to charge part order', 'error');
      console.error(err);
    }
  };

  const handleGuestPassPurchase = async () => {
    if (!guestPassForm.guestName.trim()) {
      showToast("Please enter the guest's name", 'error');
      return;
    }

    try {
      await apiService.purchaseGuestPass({
        userId: parseInt(userId),
        guestName: guestPassForm.guestName.trim()
      });

      setShowGuestPassModal(false);
      setGuestPassForm({ guestName: '' });
      loadUserData();
      showToast('Guest day pass charged successfully', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to charge guest day pass', 'error');
      console.error(err);
    }
  };

  // Tournament and service have a fixed label with optional details after it;
  // "other" is entirely what the admin types, so it is required.
  const DESK_CHARGE_LABELS = { tournament: 'Tournament fee', service: 'Service' };

  const handleDeskCharge = async () => {
    const label = DESK_CHARGE_LABELS[chargeForm.kind];
    const amount = chargeForm.kind === 'tournament' ? TOURNAMENT_FEE : parseFloat(chargeForm.amount);
    const what = chargeForm.description.trim();

    if (!(amount > 0)) {
      showToast('Please enter a valid amount', 'error');
      return;
    }
    if (!label && !what) {
      showToast('Please say what the charge is for', 'error');
      return;
    }

    let description = what;
    if (label) description = what ? `${label} - ${what}` : label;

    try {
      await apiService.deductCredits({
        userId: parseInt(userId),
        amount,
        description
      });

      setChargeForm({ kind: null, amount: '', description: '' });
      loadUserData();
      showToast(`${label || 'Charge'} of $${amount.toFixed(2)} recorded`, 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to record the charge', 'error');
      console.error(err);
    }
  };

  const handleOpenEditTransaction = (transaction) => {
    setEditTransactionForm({
      id: transaction.id,
      amount: transaction.amount,
      description: transaction.description || ''
    });
    setShowEditTransactionModal(true);
  };

  const handleEditTransaction = async () => {
    if (!editTransactionForm.amount) {
      showToast('Amount is required', 'error');
      return;
    }

    try {
      await apiService.updateTransaction(editTransactionForm.id, {
        amount: parseFloat(editTransactionForm.amount),
        description: editTransactionForm.description
      });

      setShowEditTransactionModal(false);
      setEditTransactionForm({ id: null, amount: '', description: '' });
      loadUserData();
      showToast('Transaction updated successfully', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update transaction', 'error');
      console.error(err);
    }
  };

  const handleDeleteTransaction = (transactionId) => {
    setConfirmState({
      title: 'Delete Transaction?',
      message: 'Are you sure you want to delete this transaction? This action cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          await apiService.deleteTransaction(transactionId);
          setConfirmState(null);
          loadUserData();
          showToast('Transaction deleted successfully', 'success');
        } catch (err) {
          showToast(err.response?.data?.error || 'Failed to delete transaction', 'error');
          console.error(err);
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleRecordPayment = async () => {
    try {
      await apiService.recordPayment({
        userId: parseInt(userId),
        paymentDate: paymentForm.paymentDate,
        paymentMonth: paymentForm.paymentMonth,
        amount: paymentForm.amount ? parseFloat(paymentForm.amount) : null,
        notes: paymentForm.notes
      });

      setShowPaymentModal(false);
      setPaymentForm({
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMonth: new Date().toISOString().slice(0, 7) + '-01',
        amount: '50.00',
        notes: ''
      });
      loadUserData();
      showToast('Payment recorded successfully', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to record payment', 'error');
      console.error(err);
    }
  };

  const handleOpenEditPaymentModal = (payment) => {
    setSelectedPayment(payment);
    setEditPaymentForm({
      id: payment.id,
      paymentMonth: payment.payment_month ? payment.payment_month.split('T')[0] : ''
    });
    setShowEditPaymentModal(true);
  };

  const handleEditPayment = async () => {
    try {
      await apiService.put(`/membership-payments/${editPaymentForm.id}`, {
        paymentMonth: editPaymentForm.paymentMonth
      });

      setShowEditPaymentModal(false);
      setSelectedPayment(null);
      setEditPaymentForm({ id: null, paymentMonth: '' });
      loadUserData();
      showToast('Payment month updated successfully', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update payment', 'error');
      console.error(err);
    }
  };

  const handleOpenDeletePaymentModal = (payment) => {
    setSelectedPayment(payment);
    setShowDeletePaymentModal(true);
  };

  const handleDeletePayment = async () => {
    try {
      await apiService.delete(`/membership-payments/${selectedPayment.id}`);

      setShowDeletePaymentModal(false);
      setSelectedPayment(null);
      loadUserData();
      showToast('Payment deleted successfully', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to delete payment', 'error');
      console.error(err);
    }
  };

  // Member-since is tenure, not payment status. It is worth being able to
  // correct on its own without opening the full settings sheet and risking a
  // stray change to the renewal day.
  const handleOpenMemberSince = () => {
    setMemberSinceDraft(
      membershipSettings?.start_date ? String(membershipSettings.start_date).slice(0, 10) : ''
    );
    setShowMemberSinceSheet(true);
  };

  const handleUpdateMemberSince = async () => {
    try {
      await apiService.updateMembershipSettings(userId, { memberSince: memberSinceDraft || null });
      setShowMemberSinceSheet(false);
      loadUserData();
      showToast('Member since updated', 'success');
    } catch (err) {
      showToast('Failed to update member since', 'error');
      console.error(err);
    }
  };

  const handleOpenTabSettingsModal = () => {
    setTabSettingsForm({
      tabEnabled: user?.tab_enabled || false,
      tabLimit: user?.tab_limit !== null && user?.tab_limit !== undefined ? String(user.tab_limit) : '',
      unlimited: user?.tab_limit === null && user?.tab_enabled
    });
    setShowTabSettingsModal(true);
  };

  const handleUpdateTabSettings = async () => {
    try {
      const tabLimit = tabSettingsForm.unlimited ? null :
        (tabSettingsForm.tabLimit !== '' ? parseFloat(tabSettingsForm.tabLimit) : 20);

      await apiService.updateTabSettings(userId, {
        tabEnabled: tabSettingsForm.tabEnabled,
        tabLimit: tabLimit
      });

      setShowTabSettingsModal(false);
      loadUserData();
      showToast('Tab settings updated successfully', 'success');
    } catch (err) {
      showToast('Failed to update tab settings: ' + (err.response?.data?.error || err.message), 'error');
      console.error(err);
    }
  };

  const handleOpenHandicapSeedModal = () => {
    const displaySeed = toDisplayHandicap(user?.pingolf_handicap_seed);
    setHandicapSeedForm({
      seed: displaySeed !== null ? String(Math.abs(displaySeed)) : '',
      // An unset seed opens on '+', the direction an admin reaches for most: a new player
      // good enough to spot the field. A stored 0 has no direction, so it opens there too.
      seedSign: displaySeed !== null && displaySeed < 0 ? '-' : '+',
      seedWeight: user?.pingolf_handicap_seed_weight !== null && user?.pingolf_handicap_seed_weight !== undefined ? String(user.pingolf_handicap_seed_weight) : '',
      reason: ''
    });
    setShowHandicapSeedModal(true);
  };

  const handleUpdateHandicapSeed = async () => {
    try {
      // Magnitude and sign are recombined into a display-space value, then negated back into
      // the vs-par average the server stores. Math.abs guards the case where an admin typed
      // their own minus into the number field on top of picking a sign.
      const magnitude = handicapSeedForm.seed !== '' ? Math.abs(parseFloat(handicapSeedForm.seed)) : null;
      const displaySeed = magnitude === null || Number.isNaN(magnitude)
        ? null
        : (handicapSeedForm.seedSign === '-' ? -magnitude : magnitude);
      const seed = fromDisplayHandicap(displaySeed);
      const seedWeight = handicapSeedForm.seedWeight !== '' ? parseInt(handicapSeedForm.seedWeight, 10) : undefined;

      await apiService.setPinGolfHandicapSeed(userId, {
        seed,
        seedWeight,
        reason: handicapSeedForm.reason.trim() || undefined
      });

      setShowHandicapSeedModal(false);
      loadUserData();
      showToast('PinGolf handicap seed updated successfully', 'success');
    } catch (err) {
      showToast('Failed to update handicap seed: ' + (err.response?.data?.error || err.message), 'error');
      console.error(err);
    }
  };

  const handleToggleMembership = async () => {
    try {
      await apiService.updateUser(userId, { isMember: !user.is_member });
      loadUserData();
      showToast(`User ${!user.is_member ? 'marked as member' : 'removed from members'} successfully`, 'success');
    } catch (err) {
      showToast('Failed to update membership status', 'error');
      console.error(err);
    }
  };

  const handleToggleStatus = async () => {
    try {
      await apiService.updateUser(userId, { isActive: !user.is_active });
      loadUserData();
      showToast(`User ${!user.is_active ? 'enabled' : 'disabled'} successfully`, 'success');
    } catch (err) {
      showToast('Failed to update user status', 'error');
      console.error(err);
    }
  };

  const handleToggleComplimentary = async () => {
    try {
      await apiService.updateUser(userId, { complimentaryMembership: !user.complimentary_membership });
      loadUserData();
      showToast(`Complimentary membership ${!user.complimentary_membership ? 'enabled' : 'disabled'} successfully`, 'success');
    } catch (err) {
      showToast('Failed to update complimentary membership status', 'error');
      console.error(err);
    }
  };

  const handleTechTierChange = (newTier) => {
    const tierLabels = {
      0: '0 - No Machine Access',
      1: '1 - Glass Access',
      2: '2 - Routine Maintenance',
      3: '3 - Repair Tech',
      4: '4 - Senior Tech'
    };
    setConfirmState({
      title: 'Change Tech Tier?',
      message: `Are you sure you want to change this user's tech tier to ${tierLabels[newTier]}?`,
      confirmLabel: 'Change Tier',
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          await apiService.updateUser(userId, { techTier: newTier });
          setConfirmState(null);
          loadUserData();
          showToast(`Tech tier updated to ${tierLabels[newTier]} successfully`, 'success');
        } catch (err) {
          showToast('Failed to update tech tier', 'error');
          console.error(err);
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleToggleTaskDelegate = async () => {
    try {
      if (isTaskDelegate) {
        await apiService.revokeFeatureRole(userId, 'task_management');
      } else {
        await apiService.grantFeatureRole(userId, 'task_management');
      }
      setIsTaskDelegate(!isTaskDelegate);
      showToast(`Task Delegate access ${!isTaskDelegate ? 'enabled' : 'disabled'} successfully`, 'success');
    } catch (err) {
      showToast('Failed to update Task Delegate access', 'error');
      console.error(err);
    }
  };

  const handleToggleMaintenanceOrganizer = async () => {
    try {
      if (isMaintenanceOrganizer) {
        await apiService.revokeFeatureRole(userId, 'machine_maintenance');
      } else {
        await apiService.grantFeatureRole(userId, 'machine_maintenance');
      }
      setIsMaintenanceOrganizer(!isMaintenanceOrganizer);
      showToast(`Maintenance Organizer access ${!isMaintenanceOrganizer ? 'enabled' : 'disabled'} successfully`, 'success');
    } catch (err) {
      showToast('Failed to update Maintenance Organizer access', 'error');
      console.error(err);
    }
  };

  const handleOpenAwardBadgeModal = async () => {
    try {
      const response = await apiService.getBadgeDefinitions();
      const earnedIds = new Set(userBadges.map((b) => b.id));
      const eligible = (response.data || []).filter((b) => b.is_active && !earnedIds.has(b.id));
      setAwardableBadges(eligible);
      setBadgeToAwardId('');
      setBadgeSearchTerm('');
      setShowAwardBadgeModal(true);
    } catch (err) {
      showToast('Failed to load badge catalog', 'error');
      console.error(err);
    }
  };

  const handleAwardBadge = async () => {
    if (!badgeToAwardId) {
      showToast('Please select a badge', 'error');
      return;
    }
    setAwardingBadge(true);
    try {
      await apiService.awardBadge(userId, parseInt(badgeToAwardId, 10));
      setShowAwardBadgeModal(false);
      setBadgeToAwardId('');
      loadUserData();
      showToast('Badge awarded successfully', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to award badge', 'error');
      console.error(err);
    } finally {
      setAwardingBadge(false);
    }
  };

  const handleRevokeBadge = (badge) => {
    setConfirmState({
      title: 'Revoke Badge?',
      message: `Remove "${badge.name}" from ${user.username}?`,
      confirmLabel: 'Revoke',
      danger: true,
      onConfirm: async () => {
        setConfirmSubmitting(true);
        try {
          await apiService.revokeBadge(userId, badge.id);
          setConfirmState(null);
          loadUserData();
          showToast('Badge revoked', 'success');
        } catch (err) {
          showToast(err.response?.data?.error || 'Failed to revoke badge', 'error');
          console.error(err);
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleRoleChange = (newRole) => {
    setConfirmState({
      title: 'Change Role?',
      message: `Are you sure you want to change this user's role to ${newRole}?`,
      confirmLabel: 'Change Role',
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          await apiService.updateUser(userId, { role: newRole });
          setConfirmState(null);
          loadUserData();
          showToast(`User role updated to ${newRole} successfully`, 'success');
        } catch (err) {
          showToast('Failed to update user role', 'error');
          console.error(err);
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleRegenerateQRCode = () => {
    setConfirmState({
      title: 'Regenerate QR Code?',
      message: 'Are you sure you want to generate a new QR code? The old code will stop working.',
      confirmLabel: 'Regenerate',
      danger: true,
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          await apiService.regenerateQRCode(userId);
          setConfirmState(null);
          loadUserData();
          showToast('QR code regenerated successfully', 'success');
        } catch (err) {
          showToast('Failed to regenerate QR code', 'error');
          console.error(err);
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleEditProfile = () => {
    setProfileForm({
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      phoneNumber: user.phone_number || '',
      pronouns: user.pronouns || '',
      preferredName: user.preferred_name || '',
      hostProfile: user.host_profile || '',
      sternUsername: user.stern_username || '',
      matchplayUserId: user.matchplay_user_id || '',
      ifpaPlayerId: user.ifpa_player_id || ''
    });
    setIsEditingProfile(true);
  };

  const handleCancelEditProfile = () => {
    setIsEditingProfile(false);
    setProfileForm({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      pronouns: '',
      preferredName: '',
      hostProfile: '',
      sternUsername: '',
      matchplayUserId: '',
      ifpaPlayerId: ''
    });
  };

  const handleSaveProfile = async () => {
    if (!profileForm.firstName.trim() || !profileForm.lastName.trim()) {
      showToast('First name and last name are required', 'error');
      return;
    }

    const phoneMessage = phoneError(profileForm.phoneNumber);
    if (phoneMessage) {
      showToast(phoneMessage, 'error');
      return;
    }

    try {
      // Explicitly handle all optional profile fields - send empty string if cleared, which backend converts to NULL
      const sternUsernameValue = profileForm.sternUsername?.trim();
      const pronounsValue = profileForm.pronouns?.trim();
      const preferredNameValue = profileForm.preferredName?.trim();
      const phoneNumberValue = normalizePhone(profileForm.phoneNumber);
      const hostProfileValue = profileForm.hostProfile?.trim();
      const matchplayUserIdValue = profileForm.matchplayUserId?.trim();
      const ifpaPlayerIdValue = profileForm.ifpaPlayerId?.trim();

      await apiService.updateUser(userId, {
        firstName: profileForm.firstName.trim(),
        lastName: profileForm.lastName.trim(),
        email: profileForm.email.trim(),
        phoneNumber: phoneNumberValue !== undefined ? (phoneNumberValue || '') : undefined,
        pronouns: pronounsValue !== undefined ? (pronounsValue || '') : undefined,
        preferredName: preferredNameValue !== undefined ? (preferredNameValue || '') : undefined,
        hostProfile: hostProfileValue !== undefined ? (hostProfileValue || '') : undefined,
        sternUsername: sternUsernameValue !== undefined ? (sternUsernameValue || '') : undefined,
        matchplayUserId: matchplayUserIdValue !== undefined ? (matchplayUserIdValue || '') : undefined,
        ifpaPlayerId: ifpaPlayerIdValue !== undefined ? (ifpaPlayerIdValue || '') : undefined
      });

      setIsEditingProfile(false);
      loadUserData();
      showToast('Profile updated successfully', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update profile', 'error');
      console.error(err);
    }
  };

  const handleMarkOrientationScheduled = async () => {
    try {
      await apiService.post(`/onboarding/orientation/${userId}/schedule`, {});
      showToast('Orientation marked as scheduled', 'success');
      loadUserData();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to mark orientation as scheduled', 'error');
      console.error(err);
    }
  };

  const handleMarkOrientationComplete = async () => {
    try {
      await apiService.post(`/onboarding/orientation/${userId}/complete`, {});
      showToast('Orientation marked as complete', 'success');
      loadUserData();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to mark orientation as complete', 'error');
      console.error(err);
    }
  };

  const handleMarkPaymentSetupComplete = async () => {
    try {
      await apiService.post(`/onboarding/payment-setup/${userId}/complete`, {});
      showToast('Payment setup marked as complete', 'success');
      loadUserData();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to mark payment setup as complete', 'error');
      console.error(err);
    }
  };

  const handleMarkMasterLockComplete = async () => {
    try {
      await apiService.post(`/onboarding/master-lock/${userId}/complete`, {});
      showToast('Master lock setup marked as complete', 'success');
      loadUserData();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to mark master lock setup as complete', 'error');
      console.error(err);
    }
  };

  const handleUndoOrientationScheduled = () => {
    setConfirmState({
      title: 'Undo Orientation Scheduled?',
      message: 'Are you sure you want to undo orientation scheduled? This will clear the scheduled date.',
      confirmLabel: 'Undo',
      danger: true,
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          await apiService.delete(`/onboarding/orientation/${userId}/schedule`);
          setConfirmState(null);
          showToast('Orientation schedule cleared', 'success');
          loadUserData();
        } catch (err) {
          showToast(err.response?.data?.error || 'Failed to clear orientation schedule', 'error');
          console.error(err);
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleUndoOrientationComplete = () => {
    setConfirmState({
      title: 'Undo Orientation Completion?',
      message: 'Are you sure you want to undo orientation completion? This will clear the completion date and admin.',
      confirmLabel: 'Undo',
      danger: true,
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          await apiService.delete(`/onboarding/orientation/${userId}/complete`);
          setConfirmState(null);
          showToast('Orientation completion cleared', 'success');
          loadUserData();
        } catch (err) {
          showToast(err.response?.data?.error || 'Failed to clear orientation completion', 'error');
          console.error(err);
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleUndoPaymentSetup = () => {
    setConfirmState({
      title: 'Undo Payment Setup?',
      message: 'Are you sure you want to undo payment setup? This will mark payment as incomplete.',
      confirmLabel: 'Undo',
      danger: true,
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          await apiService.delete(`/onboarding/payment-setup/${userId}/complete`);
          setConfirmState(null);
          showToast('Payment setup cleared', 'success');
          loadUserData();
        } catch (err) {
          showToast(err.response?.data?.error || 'Failed to clear payment setup', 'error');
          console.error(err);
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleUndoMasterLockSetup = () => {
    setConfirmState({
      title: 'Undo Master Lock Setup?',
      message: 'Are you sure you want to undo master lock setup? This will mark master lock as incomplete.',
      confirmLabel: 'Undo',
      danger: true,
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          await apiService.delete(`/onboarding/master-lock/${userId}/complete`);
          setConfirmState(null);
          showToast('Master lock setup cleared', 'success');
          loadUserData();
        } catch (err) {
          showToast(err.response?.data?.error || 'Failed to clear master lock setup', 'error');
          console.error(err);
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleCloseApplication = () => {
    setConfirmState({
      title: 'Close Application?',
      message: 'Are you sure you want to close this application? The user will be able to submit a new application after this is closed.',
      confirmLabel: 'Close Application',
      danger: true,
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          await apiService.put(`/onboarding/applications/${application.id}/close`);
          setConfirmState(null);
          showToast('Application closed successfully', 'success');
          loadUserData();
        } catch (err) {
          showToast(err.response?.data?.error || 'Failed to close application', 'error');
          console.error(err);
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleSwitchApplication = async (applicationId) => {
    if (applicationId === application?.id) return;

    setSelectedApplicationId(applicationId);
    try {
      // Find the selected application from history
      const selectedApp = applicationHistory.find(app => app.id === parseInt(applicationId));

      // We need to fetch full details for the selected application
      // For now, we'll reload the current application endpoint which gets the most recent
      // TODO: Create an endpoint to get application by ID with full details
      if (selectedApp) {
        // For closed applications, we show limited info from history
        // For active one, we show full details
        if (selectedApp.status === 'closed') {
          // Show limited view
          showToast('Viewing historical closed application. Full details coming soon.', 'info');
        } else {
          loadUserData(); // Reload to get full details of active application
        }
      }
    } catch (err) {
      console.error('Error switching application:', err);
    }
  };

  const handleDeleteUser = () => {
    setConfirmState({
      title: 'Delete User?',
      message: `Are you sure you want to delete user "${user.username}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          await apiService.deleteUser(userId);
          setConfirmState(null);
          showToast('User deleted successfully', 'success');
          navigate('/users');
        } catch (err) {
          showToast('Failed to delete user', 'error');
          console.error(err);
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleSyncMatchplayProfile = async () => {
    if (!user.matchplay_user_id) {
      showToast('Please add a MatchPlay User ID first', 'error');
      return;
    }

    setSyncingMatchplay(true);
    try {
      const response = await apiService.get(`/users/${userId}/matchplay-profile`);
      console.log('MatchPlay Profile Response:', response.data);

      setMatchplayProfile(response.data);

      // Extract IFPA ID from the actual response structure
      const ifpaId = response.data?.rawProfile?.user?.ifpaId;

      // If IFPA ID was synced and user doesn't have one set, update automatically
      if (ifpaId && !user.ifpa_player_id) {
        console.log('Auto-populating IFPA ID:', ifpaId);
        await apiService.updateUser(userId, {
          ifpaPlayerId: String(ifpaId)
        });
        await loadUserData();
        showToast('MatchPlay profile synced successfully! IFPA ID has been auto-populated.', 'success');
      } else {
        console.log('IFPA ID not auto-populated. ifpaId:', ifpaId, 'existing:', user.ifpa_player_id);
        showToast('MatchPlay profile synced successfully!', 'success');
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to sync MatchPlay profile', 'error');
      console.error('MatchPlay sync error:', err);
    } finally {
      setSyncingMatchplay(false);
    }
  };

  const handleResetPassword = () => {
    setConfirmState({
      title: 'Reset Password?',
      message: `Are you sure you want to reset the password for "${user.username}"? A new temporary password will be generated.`,
      confirmLabel: 'Reset Password',
      danger: true,
      onConfirm: async () => {
        setConfirmSubmitting(true);
        try {
          const response = await apiService.resetUserPassword(userId);
          setPasswordResetResult(response.data);
          setConfirmState(null);
          setShowPasswordResetModal(true);
        } catch (err) {
          showToast('Failed to reset password: ' + (err.response?.data?.error || err.message), 'error');
          console.error(err);
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  // Formats in UTC. "Member since" is a DATE column, and rendering it in the
  // browser's zone showed everyone west of UTC the day before.
  const formatDate = (dateString) => formatDateOnly(dateString, 'N/A');

  const formatMonth = (dateString) => {
    if (!dateString) return 'N/A';
    // Parse date string as YYYY-MM-DD and extract year/month directly to avoid timezone issues
    const [year, month] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getNextRenewalDate = (renewalDay) => {
    if (!renewalDay) return 'Not Set';

    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const currentDay = today.getDate();

    let nextRenewalMonth = currentMonth;
    let nextRenewalYear = currentYear;

    if (currentDay > renewalDay) {
      nextRenewalMonth += 1;
      if (nextRenewalMonth > 11) {
        nextRenewalMonth = 0;
        nextRenewalYear += 1;
      }
    }

    const nextRenewalDate = new Date(nextRenewalYear, nextRenewalMonth, renewalDay);
    return nextRenewalDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return {
    // core data
    user, membershipSettings, application, applicationHistory, selectedApplicationId,
    signedDocuments,
    memberEmails,
    transactions, paymentHistory, loading, error,
    isTaskDelegate, isMaintenanceOrganizer,
    userBadges, awardableBadges, setAwardableBadges,
    showAwardBadgeModal, setShowAwardBadgeModal,
    badgeToAwardId, setBadgeToAwardId,
    badgeSearchTerm, setBadgeSearchTerm,
    awardingBadge,

    // view control state
    showAllTransactions, setShowAllTransactions,
    showMobileTransactions, setShowMobileTransactions,
    showAllPayments, setShowAllPayments,
    showPaymentHistory, setShowPaymentHistory,

    // modal state
    showCreditsModal, setShowCreditsModal,
    showPaymentModal, setShowPaymentModal,
    showPasswordResetModal, setShowPasswordResetModal,
    showTabSettingsModal, setShowTabSettingsModal,
    showHandicapSeedModal, setShowHandicapSeedModal,
    showPartOrderModal, setShowPartOrderModal,
    showGuestPassModal, setShowGuestPassModal,
    showEditTransactionModal, setShowEditTransactionModal,
    showEditPaymentModal, setShowEditPaymentModal,
    showDeletePaymentModal, setShowDeletePaymentModal,
    selectedPayment, setSelectedPayment,
    confirmState, setConfirmState,
    confirmSubmitting,

    // profile edit state
    isEditingProfile, setIsEditingProfile,
    profileForm, setProfileForm,

    // password reset
    passwordResetResult, setPasswordResetResult,

    // pin golf

    // part order
    partOrderForm, setPartOrderForm,

    // guest pass
    guestPassForm, setGuestPassForm,

    // tournament fee / other desk charge
    chargeForm, setChargeForm,

    // matchplay
    matchplayProfile, syncingMatchplay,

    // forms
    creditsForm, setCreditsForm,
    editTransactionForm, setEditTransactionForm,
    paymentForm, setPaymentForm,
    editPaymentForm, setEditPaymentForm,
    tabSettingsForm, setTabSettingsForm,
    handicapSeedForm, setHandicapSeedForm,

    // functions
    loadUserData,
    handleAddCredits,
    handlePartOrderCharge,
    handleGuestPassPurchase,
    handleDeskCharge,
    handleOpenEditTransaction,
    handleEditTransaction,
    handleDeleteTransaction,
    handleRecordPayment,
    handleOpenEditPaymentModal,
    handleEditPayment,
    handleOpenDeletePaymentModal,
    handleDeletePayment,
    showMemberSinceSheet, setShowMemberSinceSheet,
    memberSinceDraft, setMemberSinceDraft,
    handleOpenMemberSince,
    handleUpdateMemberSince,
    handleOpenTabSettingsModal,
    handleUpdateTabSettings,
    handleOpenHandicapSeedModal,
    handleUpdateHandicapSeed,
    handleToggleMembership,
    handleToggleStatus,
    handleToggleComplimentary,
    handleTechTierChange,
    handleToggleTaskDelegate,
    handleToggleMaintenanceOrganizer,
    handleOpenAwardBadgeModal,
    handleAwardBadge,
    handleRevokeBadge,
    handleRoleChange,
    handleRegenerateQRCode,
    handleEditProfile,
    handleCancelEditProfile,
    handleSaveProfile,
    handleMarkOrientationScheduled,
    handleMarkOrientationComplete,
    handleMarkPaymentSetupComplete,
    handleMarkMasterLockComplete,
    handleUndoOrientationScheduled,
    handleUndoOrientationComplete,
    handleUndoPaymentSetup,
    handleUndoMasterLockSetup,
    handleCloseApplication,
    handleSwitchApplication,
    handleDeleteUser,
    handleSyncMatchplayProfile,
    handleResetPassword,
    formatDate,
    formatMonth,
    formatDateTime,
    getNextRenewalDate
  };
}
